import React from 'react';
import { DriverRegistrationPortalNoAuthService } from '@gen/driverRegistration/driverRegistrationPortalNoAuth';
import {
  getFinalScreen,
  getRegistrationStatus,
  getIsLeadHomeScreenEnabled,
  getTranslations,
  isVehicleMarketplaceEnabled,
} from '../../redux/form/formSelectors';
import { useSelector } from '../../redux/store';
import { SubmittedFinalScreen } from './SubmittedFinalScreen';
import { AcceptedFinalScreen } from './AcceptedFinalScreen';
import {
  ACCEPTED_STATUSES,
  DECLINED_STATUSES,
  SUBMITTED_STATUSES,
} from '../../const/statuses';
import { DeclinedFinalScreen } from './DeclinedFinalScreen';
import { VehicleMarketplaceEntryScreen } from './VehicleMarketplaceEntryScreen';
import { SubmittedFinalScreenForEarlyAppAccess } from './SubmittedFinalScreenForEarlyAppAccess';
import { getAppVersion, getIsNativeApp } from '../../common/helpers';

function getMajorVersion(
  input: string | null | undefined
): { platform: 'DA' | 'DI'; version: number } | null {
  if (!input) return null;

  const match = /^(DA|DI)\.(\d+)\.\d+$/i.exec(input);
  if (!match) return null;

  const platform = match[1].toUpperCase() as 'DA' | 'DI';
  const version = parseInt(match[2], 10);

  return { platform, version };
}

export const FinalScreen: React.FC = () => {
  const commonTranslations = useSelector(getTranslations);
  const DefaultFinalScreen: DriverRegistrationPortalNoAuthService.FinalScreen =
    {
      translations: {
        title: commonTranslations.generic_final_screen.title,
        subtitle: commonTranslations.generic_final_screen.subtitle,
        label: commonTranslations.generic_final_screen.title,
      },
    };

  const finalScreen = useSelector(getFinalScreen) || DefaultFinalScreen;
  const status = useSelector(getRegistrationStatus);
  const showVehicleMarketplaceScreen = useSelector(isVehicleMarketplaceEnabled);
  const isLeadHomeScreenEnabled = useSelector(getIsLeadHomeScreenEnabled);

  const isOpenedFromNativeAppFlow = getIsNativeApp();

  // We allow only supported app version and browsers without app version
  const appVersionInfo = getMajorVersion(getAppVersion());
  const isAppVersionOutdated =
    appVersionInfo &&
    ((appVersionInfo.platform === 'DA' && appVersionInfo.version < 119) ||
      (appVersionInfo.platform === 'DI' && appVersionInfo.version < 118));

  const shouldShowLeadHomeScreen =
    !isAppVersionOutdated &&
    (isLeadHomeScreenEnabled || isOpenedFromNativeAppFlow);

  const screen = React.useMemo(() => {
    if (ACCEPTED_STATUSES.includes(status)) {
      return <AcceptedFinalScreen />;
    }
    if (DECLINED_STATUSES.includes(status)) {
      return <DeclinedFinalScreen />;
    }
    if (SUBMITTED_STATUSES.includes(status)) {
      if (showVehicleMarketplaceScreen) {
        return <VehicleMarketplaceEntryScreen />;
      }
      if (shouldShowLeadHomeScreen) {
        return <SubmittedFinalScreenForEarlyAppAccess />;
      }
      return <SubmittedFinalScreen finalScreen={finalScreen} />;
    }
    return null;
  }, [
    finalScreen,
    shouldShowLeadHomeScreen,
    showVehicleMarketplaceScreen,
    status,
  ]);
  return <>{screen}</>;
};
